/* eslint-disable lyrahealth/no-react-native-text */
import React from 'react'

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { isNil } from 'lodash-es'

import {
  audInformedConsent,
  cleInformedConsent,
  coachingInformedConsent,
  coachingInformedConsentNonUS,
  daInformedConsent,
  dbtTeensInformedConsent,
  informedConsent,
  mcInformedConsent,
  medsInformedConsent,
  sapInformedConsent,
  singleSessionCoachingInformedConsent,
  teensTherapyInformedConsent,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/constants'
import { getVisibleFields } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import {
  FieldSchema,
  FieldValue,
  UiMetadata,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

type FormItemPDFProps = {
  formBody?: { [key: string]: FieldValue }
  formUISchema?: UiMetadata
  formSchema?: FieldSchema
  formUISchemaOrder: string[]
  externalValues?: { [key: string]: FieldValue }
}

const styles = StyleSheet.create({
  formContent: {
    marginTop: 8,
    minHeight: '100px',
  },
  fieldContainer: {
    marginBottom: 12,
  },
  header: {
    color: lightThemeColors.textPrimary,
    marginBottom: 8,
  },
  boldHeader: {
    fontWeight: 'bold',
  },
  fieldLabel: {
    fontSize: 11,
    color: lightThemeColors.textPrimary,
    marginBottom: 4,
  },
  fieldValue: {
    paddingTop: 4,
    fontSize: 11,
    color: lightThemeColors.textSecondary,
    lineHeight: 1.2,
  },
  spacing: {
    height: 12,
  },
  careConsentContainer: {
    marginTop: 12,
    border: `1px solid ${lightThemeColors.borderDefault}`,
    borderRadius: 8,
    padding: 12,
  },
  careConsentSection: {
    color: lightThemeColors.textPrimary,
    fontSize: 11,
    lineHeight: 1.2,
  },
  valueExplanationContainer: {
    backgroundColor: lightThemeColors.backgroundElement,
    padding: 8,
    marginTop: 8,
    borderRadius: 4,
  },
  correctEntryLabel: {
    fontSize: 12,
    color: lightThemeColors.textSuccess,
    marginBottom: 4,
  },
  incorrectEntryLabel: {
    fontSize: 12,
    color: lightThemeColors.textError,
    marginBottom: 4,
  },
  valueDetails: {
    fontSize: 10,
    color: lightThemeColors.textSecondary,
    lineHeight: 1.2,
  },
  inProgressForm: {
    fontSize: 11,
    color: lightThemeColors.textSecondary,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: 16,
  },
})

const renderStyledSubstring = (input: string) => {
  const splitByHeadingsAndText = (input: string): string[] => {
    const substrings: string[] = []

    let buffer = ''

    const trimmed = input.trim()

    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed)

    if (headingMatch) {
      const hashes = headingMatch[1]
      let content = headingMatch[2]

      content = content.replace(/\s*#+\s*$/, '').trim()

      if (buffer) {
        substrings.push(buffer.trim())
        buffer = ''
      }

      substrings.push(`${hashes} ${content}`)
    } else {
      buffer += (buffer ? '\n' : '') + trimmed
    }

    if (buffer) {
      substrings.push(buffer.trim())
    }

    return substrings
  }

  const renderBoldOrItalizedSubstring = (text: string, baseStyle: any, key: string | number) => {
    const parts = text.split(/(__[^_\s][^_]*[^_\s]__|__[^_\s]__|__[^_]*__|\*+[^\s*][^*]*[^\s*]\*+|\*[^\s*]\*)/g)

    return (
      <Text key={key} style={baseStyle}>
        {parts.map((part, partIndex) => {
          const underscoreItalicMatch = part.match(/^__+([^_\s][^_]*[^_\s]|[^_\s]|[^_]*)__+$/)
          if (underscoreItalicMatch) {
            return (
              <Text key={partIndex} style={{ fontStyle: 'italic' }}>
                {underscoreItalicMatch[1]}
              </Text>
            )
          }

          const asteriskBoldMatch = part.match(/^\*+([^\s*][^*]*[^\s*]|\S)\*+$/)
          if (asteriskBoldMatch) {
            return (
              <Text key={partIndex} style={{ fontWeight: 'bold' }}>
                {asteriskBoldMatch[1]}
              </Text>
            )
          }

          return <Text key={partIndex}>{part}</Text>
        })}
      </Text>
    )
  }

  const substrings = splitByHeadingsAndText(input)

  return substrings.map((text, index) => {
    const headingMatch = text.match(/^(#+)\s*(.*)$/)
    if (headingMatch) {
      const [, hashes, content] = headingMatch
      const level = Math.min(hashes.length, 6)
      return renderBoldOrItalizedSubstring(
        content,
        [styles.header, { fontSize: 24 - level * 2 }, ...(level <= 2 ? [styles.boldHeader] : [])],
        index,
      )
    }
    return renderBoldOrItalizedSubstring(text, styles.fieldLabel, index)
  })
}

const renderCareConsentText = (input: string) => {
  const sections = input.split(/\n\s*\n/)

  const renderTextWithBullets = (text: string, baseStyle: any, key: string | number) => {
    const lines = text.split('\n')
    const processedLines: React.ReactNode[] = []

    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim()

      const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/)
      if (headingMatch) {
        const [, hashes, content] = headingMatch
        const level = Math.min(hashes.length, 6)

        const parts = content.split(/(\*[^\s*][^*]*[^\s*]\*|\*[^\s*]\*)/g)
        const headingLine = (
          <Text
            key={`${key}-heading-${lineIndex}`}
            style={[
              styles.header,
              styles.boldHeader,
              { fontSize: 24 - level * 2, marginBottom: 8, marginTop: lineIndex > 0 ? 12 : 0 },
            ]}
          >
            {parts.map((part, partIndex) => {
              const boldMatch = part.match(/^\*([^\s*][^*]*[^\s*]|\S)\*$/)
              if (boldMatch) {
                return (
                  <Text key={partIndex} style={{ fontWeight: 'bold' }}>
                    {boldMatch[1]}
                  </Text>
                )
              }
              return part
            })}
          </Text>
        )
        processedLines.push(headingLine)
        return
      }

      const bulletMatch = line.match(/^(\s*)\*\s+(.*)$/)
      if (bulletMatch) {
        const [, indentation, content] = bulletMatch
        const bulletText = `${indentation}• ${content}`

        const parts = bulletText.split(/(\*[^\s*][^*]*[^\s*]\*|\*[^\s*]\*)/g)
        const bulletLine = (
          <Text key={`${key}-bullet-${lineIndex}`} style={[baseStyle, { marginBottom: 4 }]}>
            {parts.map((part, partIndex) => {
              const boldMatch = part.match(/^\*([^\s*][^*]*[^\s*]|\S)\*$/)
              if (boldMatch) {
                return (
                  <Text key={partIndex} style={{ fontWeight: 'bold' }}>
                    {boldMatch[1]}
                  </Text>
                )
              }
              return part
            })}
          </Text>
        )
        processedLines.push(bulletLine)
      } else {
        const parts = line.split(/(\*[^\s*][^*]*[^\s*]\*|\*[^\s*]\*)/g)
        const regularLine = (
          <Text key={`${key}-line-${lineIndex}`} style={baseStyle}>
            {parts.map((part, partIndex) => {
              const boldMatch = part.match(/^\*([^\s*][^*]*[^\s*]|\S)\*$/)
              if (boldMatch) {
                return (
                  <Text key={partIndex} style={{ fontWeight: 'bold' }}>
                    {boldMatch[1]}
                  </Text>
                )
              }
              return part
            })}
          </Text>
        )
        processedLines.push(regularLine)
      }
    })

    return <>{processedLines}</>
  }

  return sections.map((section, index) => {
    const trimmedSection = section.trim()
    if (!trimmedSection) return <></>

    return (
      <View key={index} style={{ marginBottom: 12, marginTop: index > 0 ? 4 : 0 }}>
        {renderTextWithBullets(trimmedSection, styles.careConsentSection, index)}
      </View>
    )
  })
}

export const renderStyledStrings = (input: string) => {
  const lines = input.split('\n')
  return (
    <View>
      {lines.map((line, index) => {
        if (line === '') {
          return <View style={styles.spacing} key={index} />
        }
        return (
          <Text key={index} style={styles.fieldValue}>
            {renderStyledSubstring(line)}
          </Text>
        )
      })}
    </View>
  )
}

const getFieldDisplayValue = ({
  formFieldValue,
  fieldSchema,
  widgetType,
}: {
  formFieldValue: any
  fieldSchema: FieldSchema | undefined
  widgetType: string
}) => {
  let fieldValue = formFieldValue
  if (widgetType === 'checkboxGroup') {
    fieldValue = formFieldValue
      .map((value: string) =>
        fieldSchema?.enum?.indexOf(value) !== undefined
          ? fieldSchema?.enumNames
            ? fieldSchema?.enumNames?.[fieldSchema?.enum?.indexOf(value)]
            : value
          : '',
      )
      .filter((value: string) => value !== '')
      .join(', ')
  } else if (widgetType === 'radio') {
    fieldValue =
      fieldSchema?.enum?.indexOf(formFieldValue) !== undefined
        ? fieldSchema?.enumNames
          ? fieldSchema?.enumNames?.[fieldSchema?.enum?.indexOf(formFieldValue)]
          : formFieldValue
        : formFieldValue === 0
          ? 'No'
          : 'Yes'
  } else if (widgetType === 'checkbox') {
    fieldValue = formFieldValue ? 'Yes' : 'No'
  } else if (fieldSchema?.type === 'boolean') {
    fieldValue = formFieldValue ? 'Yes' : 'No'
  } else if (widgetType === 'typeAhead') {
    if (Array.isArray(formFieldValue)) {
      fieldValue = formFieldValue
        .map((item: string | { label: string }) => (typeof item === 'string' ? item : item?.label))
        .join(', ')
    } else {
      fieldValue = formFieldValue
    }
  }
  return fieldValue
}

const careConsents = {
  InformedConsent: informedConsent,
  MedsInformedConsent: medsInformedConsent,
  CLEInformedConsent: cleInformedConsent,
  SAPInformedConsent: sapInformedConsent,
  CoachingInformedConsent: coachingInformedConsent,
  CoachingInformedConsentNonUS: coachingInformedConsentNonUS,
  SingleSessionCoachingInformedConsent: singleSessionCoachingInformedConsent,
  AUDInformedConsent: audInformedConsent,
  TeensTherapyInformedConsent: teensTherapyInformedConsent,
  DirectAccessInformedConsent: daInformedConsent,
  DbtTeensInformedConsent: dbtTeensInformedConsent,
  McInformedConsent: mcInformedConsent,
}

const FormField: React.FC<{
  formFieldLabel: string
  formFieldValue: any
  widgetType: string
  fieldSchema?: FieldSchema
}> = ({ formFieldLabel, formFieldValue, widgetType, fieldSchema }) => {
  const isContentField = fieldSchema?.content
  const isCareConsent = fieldSchema?.name === 'informedConsent' || fieldSchema?.name === 'informedConsentInfo'

  if (
    (!isContentField && !isCareConsent && isNil(formFieldValue)) ||
    (typeof formFieldValue === 'string' && formFieldValue.trim() === '')
  ) {
    return null
  }

  let displayLabel: string = ''
  let displayValueText: string | undefined = ''
  let careConsentText = undefined

  if (isContentField) {
    displayLabel =
      (fieldSchema.content as string) ||
      formFieldLabel
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    displayValueText = ''
  } else {
    displayLabel =
      (fieldSchema?.title as string) ||
      formFieldLabel
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    displayValueText = getFieldDisplayValue({ formFieldValue, fieldSchema, widgetType })
  }
  if (isCareConsent) {
    displayLabel =
      (fieldSchema?.title as string) ||
      formFieldLabel
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    careConsentText = careConsents[widgetType as keyof typeof careConsents]
  }

  const hasCorrectValue = fieldSchema?.correctValue !== undefined
  const correctValueDetails = hasCorrectValue ? fieldSchema?.correctValueDetails : undefined

  return (
    <View style={styles.fieldContainer}>
      {renderStyledStrings(displayLabel)}
      {displayValueText && <Text style={styles.fieldValue}>{displayValueText}</Text>}
      {hasCorrectValue && (
        <View style={styles.valueExplanationContainer}>
          {fieldSchema?.correctValue === formFieldValue ? (
            <Text style={styles.correctEntryLabel}>Correct Entry</Text>
          ) : (
            <Text style={styles.incorrectEntryLabel}>Incorrect Entry</Text>
          )}
          {correctValueDetails && <Text style={styles.valueDetails}>{correctValueDetails}</Text>}
        </View>
      )}
      {careConsentText && <View style={styles.careConsentContainer}>{renderCareConsentText(careConsentText)}</View>}
    </View>
  )
}

const FormItemPDF: React.FC<FormItemPDFProps> = ({
  formBody,
  formUISchema,
  formSchema,
  formUISchemaOrder,
  externalValues,
}) => {
  if (!formBody || !formUISchema || !formSchema || !formUISchemaOrder) {
    return null
  }
  // Exported notes are signed, and everyone who can see a signed note reads the licensed-only (risk) fields.
  const visibleFields = getVisibleFields({
    uiSchema: formUISchema,
    schema: formSchema,
    values: { ...formBody, isLicensed: true, ...externalValues },
  })

  const visibleFieldOrder = formUISchemaOrder.filter((fieldName: string) => visibleFields.includes(fieldName))

  return (
    <View style={styles.formContent}>
      {visibleFieldOrder &&
        visibleFieldOrder.map((formFieldLabel: string) => {
          return (
            <FormField
              key={formFieldLabel}
              formFieldLabel={formFieldLabel}
              formFieldValue={formBody?.[formFieldLabel]}
              widgetType={formUISchema?.[formFieldLabel]?.['ui:widget'] ?? formUISchema?.[formFieldLabel]?.['ui:field']}
              fieldSchema={formSchema?.properties?.[formFieldLabel]}
            />
          )
        })}
    </View>
  )
}

export default FormItemPDF
